import { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, DialogActions } from "@mui/material";
import { SystemUpdateAlt } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { Dealer, DealerMasterDealer } from "@/interfaces";
import { baseUrl } from "@/consts";
import { formatAxiosError } from "@/utils";
import { colors, sxButton } from "@/theme";
import { DebounceInput, UploadFileInput } from "@/components/atoms";
import {
  AlertDialog,
  CrudGrid,
  DealerEditDialog,
} from "@/components/organisms";
import { MuiHelper } from "@/helpers";
import { CrudGridRef } from "@/components/organisms/CrudGrid/interface";
import { useDataApi } from "@/hooks";

const offset = 100000;
const getSurrogateKey = (id: number, subId: number): number =>
  id * offset + subId;
const getId = (surrogateKey: number): number =>
  Math.floor(surrogateKey / offset);

const columns: GridColDef[] = [
  { field: "firstName", headerName: "First Name", flex: 1 },
  { field: "lastName", headerName: "Last Name", flex: 1 },
  { field: "dealerIdentifier", headerName: "Badge ID", flex: 1 },
  { field: "email", headerName: "Email", flex: 1 },
  { field: "phone", headerName: "Phone", flex: 1 },
  { field: "company", headerName: "Company", flex: 1 },
  { field: "masterDealerId", headerName: "Master Dealer ID", flex: 1 },
  { field: "channel", headerName: "Channel", flex: 1 },
  { field: "acbAllowance", headerName: "POS Allowance", flex: 1 },
  { field: "percentageOff", headerName: "Custom Rate", flex: 1  },
];

const Dealers: React.FC = () => {
  const childRef = useRef<CrudGridRef | null>(null);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [uploaded, setUploaded] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [isDealerEditDialogOpen, setIsDealerEditDialogOpen] = useState(false);
  const [dealerToEdit, setDealerToEdit] = useState<Dealer | undefined>(
    undefined
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [dealerToDelete, setDealerToDelete] = useState<DealerMasterDealer | undefined>(undefined);
  const { _delete } = useDataApi(baseUrl.DEALER_DEALER_MASTER_DEALER);

  useEffect(() => {
    if (!uploaded || (uploaded as any)?.status !== 200) {
      return;
    }

    alert("Your attendees have been uploaded");
    setUploaded(undefined);
    reloadData();
  }, [uploaded]);

  const reloadData = () => {
    childRef?.current?.reloadData?.();
  };

  const editDealer = (dealer?: DealerMasterDealer) => {
    setDealerToEdit(dealer);
    setIsDealerEditDialogOpen(true);
  };

  const onDealerEditDialogClose = (dealer?: Dealer) => {
    setIsDealerEditDialogOpen(false);
    setDealerToEdit(undefined);

    if (dealer) {
      reloadData();
    }
  };

  const onDeleteRequest = (dealer: DealerMasterDealer) => {
    setIsDealerEditDialogOpen(false);
    setDealerToEdit(undefined);
    setDealerToDelete(dealer);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!dealerToDelete?.dealerId) return;
    await _delete?.(`${baseUrl.DEALER_DEALER_MASTER_DEALER}/${dealerToDelete.dealerId}0${dealerToDelete.dealerMasterDealersId}`);
    setIsDeleteDialogOpen(false);
    setDealerToDelete(undefined);
    reloadData();
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setDealerToDelete(undefined);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: 'center', justifyContent: 'space-between', p:4 }}>
        {MuiHelper.getHeader("Attendees")}
        <Box sx={{ justifySelf: "end" }}>
          <Button onClick={() => editDealer()} sx={{ backgroundColor: colors.primaryOzone500, color: colors.white, px: 3, py: 1.5, borderRadius: 3, fontFamily: 'PF DIN Text Pro', fontSize: 16 }}>
            Add Attendee
          </Button>
        </Box>
      </Box>
      <Box>
        <Box
          // sx={{
          //   display: "flex",
          //   justifyContent: "flex-end",
          //   alignItems: "center",
          //   padding: "10px",
          // }}
        >
          <Box sx={{ justifySelf: "end" }}>
            {error ? (
              <AlertDialog
                message={formatAxiosError(error)}
                title="Error"
                confirmText="Ok"
                severity="error"
              />
            ) : null}
          </Box>
        </Box>
        <Box sx={{ backgroundColor: colors.neutralGrey300, padding: 3 }}>
          <CrudGrid
            ref={childRef}
            baseUrl={baseUrl.DEALER_DEALER_MASTER_DEALER}
            columns={columns}
            getRowId={(row) =>
              getSurrogateKey(row.dealerId, row.dealerMasterDealersId)
            }
            setRowId={(row, id) => {
              row.dealerId = getId(id);
            }}
            search={search}
            onEditRow={editDealer}
            shouldShowDelete={false}
            bulkImportButton={{
              url: `${baseUrl.DEALER}/upload`,
              text: "Bulk Import",
              endIcon: <SystemUpdateAlt />,
              onUploaded: setUploaded,
              onError: setError,
              sxButton: sxButton
            }}
          />
        </Box>
      </Box>
      <DealerEditDialog
        dealer={dealerToEdit}
        open={isDealerEditDialogOpen}
        onClose={onDealerEditDialogClose}
        onDeleteRequest={onDeleteRequest}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <DialogTitle sx={{ fontWeight: 500, fontSize: 22, color: '#2C3856', mb: 1, px: 0 }}>
            Are you sure you want to delete this attendee?
          </DialogTitle>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              color: '#2C3856',
              borderColor: '#B0B7C3',
              background: '#fff',
              fontWeight: 500,
              fontSize: 18,
              width: 140,
              height: 48,
              mr: 2,
              textTransform: 'none',
              fontFamily: 'PF DIN Text Pro',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              background: '#E5391B',
              color: '#fff',
              fontWeight: 600,
              fontSize: 18,
              width: 140,
              height: 48,
              textTransform: 'none',
              fontFamily: 'PF DIN Text Pro',
              boxShadow: 'none',
              '&:hover': { background: '#b71c1c' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dealers;
